import { createSelector } from '@reduxjs/toolkit';
import qs from 'querystring';

import type { ReduxApplicationState } from 'src/redux/combinedReducer';

export const selectFiltersWithActiveOptions = (state: ReduxApplicationState) => {
  return (state.productsQuery.filters || []).filter((filter) => {
    return filter.options.some((option) => option.active);
  });
};

export const selectLastPageLoaded = (state: ReduxApplicationState) =>
  state.products.lastPageLoaded || 0;

export const selectTotalPages = (state: ReduxApplicationState) => state.products.totalPages;

export const selectTotalProducts = (state: ReduxApplicationState) => state.products.totalProducts;

export const selectPartnerShortcode = ({ products }: ReduxApplicationState) =>
  products.partner?.shortcode ?? '';

export const selectPartnerName = ({ products }: ReduxApplicationState) =>
  products.partner?.name ?? '';

export const selectPartnerMetaTagDescription = ({ products }: ReduxApplicationState) =>
  products.partner?.metaTagDescription ?? '';

export const selectPartnerLogoUrl = ({ products }: ReduxApplicationState) =>
  products.partner?.logo ?? '';

const PARTNER_HOMEPAGE_LINK_INDEX = 0;
const PARTNER_PRODUCTS_LINK_INDEX = 1;

export const selectPartnerHomepageUrl = ({ products }: ReduxApplicationState) =>
  products.partner?.links[PARTNER_HOMEPAGE_LINK_INDEX]?.href ?? '';

export const selectPartnerHomepageRelativeUrl = ({ products }: ReduxApplicationState) =>
  products.partner?.links[PARTNER_HOMEPAGE_LINK_INDEX]?.rel ?? '';

export const selectPartnerProductsUrl = ({ products }: ReduxApplicationState) =>
  products.partner?.links[PARTNER_PRODUCTS_LINK_INDEX]?.href ?? '';

export const selectProductFromCode = createSelector(
  [
    ({ products }: ReduxApplicationState) => products.pages,
    (_, productCode: number) => productCode,
  ],
  (products, productCode) =>
    Object.values(products)
      .flat()
      .find(({ code }) => code === productCode),
);

export const selectProviderAttributionToken = ({ products }: ReduxApplicationState) =>
  products.providerAttributionToken;

export const selectProviderMetadata = ({ products }: ReduxApplicationState) =>
  products.providerMetadata;

export const selectRedirectUrl = ({ products, router }: ReduxApplicationState) => {
  const { pathname, search } = router.location;
  const { redirectUrl, totalPages } = products;

  if (redirectUrl) {
    return redirectUrl;
  }

  const query = qs.parse(search.startsWith('?') ? search.slice(1) : search);
  let page = 1;
  if (query.page) {
    const pageValue = Array.isArray(query.page) ? query.page[0] : query.page;
    page = !isNaN(Number(pageValue)) ? Number(pageValue) : 1;
  }

  if (totalPages > 0 && page > totalPages) {
    delete query.page;

    return `${pathname}?${qs.stringify(query)}`;
  }

  return null;
};
